
import { create } from 'zustand';
import {
  ActivityEntry,
  ChecklistItem,
  CHECKLIST_TEMPLATE,
  ID,
  Lead,
  LinkRef,
  NoteEntry,
  Person,
  Proposal,
  ProposalStage,
  PropertyRecord,
  SocialChannel,
  SocialPost,
  StatusKey,
} from '../types';
import { SEED_LEADS, SEED_PEOPLE, SEED_PROPERTIES, SEED_PROPOSALS, SEED_SOCIAL, SEED_ACTIVITY } from '../data/seed';

const uid = () => Math.random().toString(36).slice(2, 10);
const now = () => new Date().toISOString();

// The three principals that get notified on every request
export const PRINCIPAL_IDS = ['faraz', 'michael', 'simon'];

interface Notification {
  id: ID;
  text: string;
  createdAt: string;
  read: boolean;
  proposalId?: ID;
}

interface WorkspaceState {
  currentUserId: ID;
  people: Person[];
  proposals: Proposal[];
  properties: PropertyRecord[];
  leads: Lead[];
  social: SocialPost[];
  activity: ActivityEntry[];
  notifications: Notification[];

  // helpers
  personById: (id?: ID) => Person | undefined;

  // proposals
  createProposal: (input: {
    property: string;
    address: string;
    assetType: string;
    deckType: Proposal['deckType'];
    requestedById: ID;
  }) => string;
  setItemStatus: (proposalId: ID, itemKey: string, status: StatusKey) => void;
  addItemLink: (proposalId: ID, itemKey: string, link: Omit<LinkRef, 'id' | 'addedAt' | 'addedById'>) => void;
  addItemNote: (proposalId: ID, itemKey: string, text: string) => void;
  setProposalStage: (proposalId: ID, stage: ProposalStage) => void;

  // properties
  togglePropertyPlatform: (propertyId: ID, platform: string) => void;
  setPropertyStatus: (propertyId: ID, status: PropertyRecord['status']) => void;

  // leads
  setLeadStatus: (leadId: ID, status: Lead['status']) => void;

  // social
  addSocialPost: (input: Omit<SocialPost, 'id'>) => void;
  updateSocialStatus: (postId: ID, status: SocialPost['status']) => void;
  moveSocialPost: (postId: ID, date: string) => void;

  markAllNotificationsRead: () => void;
}

function buildChecklist(): ChecklistItem[] {
  return CHECKLIST_TEMPLATE.map((t) => ({
    id: uid(),
    key: t.key,
    label: t.label,
    hint: t.hint,
    status: 'not_started' as StatusKey,
    links: [],
    notes: [],
  }));
}

export const useWorkspace = create<WorkspaceState>((set, get) => ({
  currentUserId: 'faraz',
  people: SEED_PEOPLE,
  proposals: SEED_PROPOSALS,
  properties: SEED_PROPERTIES,
  leads: SEED_LEADS,
  social: SEED_SOCIAL,
  activity: SEED_ACTIVITY,
  notifications: [
    { id: uid(), text: 'Simon Vega marked Offering Memorandum as Done on Harborview Tower', createdAt: new Date(Date.now() - 3600e3).toISOString(), read: false, proposalId: 'p1' },
    { id: uid(), text: 'Dana Ruiz requested a BOV for 5th & Main Retail', createdAt: new Date(Date.now() - 8600e3).toISOString(), read: false, proposalId: 'p3' },
  ],

  personById: (id) => get().people.find((p) => p.id === id),

  createProposal: (input) => {
    const id = uid();
    const proposal: Proposal = {
      id,
      property: input.property,
      address: input.address,
      assetType: input.assetType,
      deckType: input.deckType,
      requestedById: input.requestedById,
      requestedAt: now(),
      stage: 'requested',
      checklist: buildChecklist(),
    };
    const broker = get().personById(input.requestedById);
    const activity: ActivityEntry = {
      id: uid(),
      proposalId: id,
      authorId: input.requestedById,
      text: `requested a ${input.deckType} for ${input.property}`,
      createdAt: now(),
    };
    set((s) => ({
      proposals: [proposal, ...s.proposals],
      activity: [activity, ...s.activity],
      notifications: [
        {
          id: uid(),
          text: `${broker?.name ?? 'A broker'} requested a ${input.deckType} for ${input.property} — Faraz, Michael & Simon notified`,
          createdAt: now(),
          read: false,
          proposalId: id,
        },
        ...s.notifications,
      ],
    }));
    return id;
  },

  setItemStatus: (proposalId, itemKey, status) => {
    const user = get().currentUserId;
    set((s) => ({
      proposals: s.proposals.map((p) => {
        if (p.id !== proposalId) return p;
        const checklist = p.checklist.map((it) =>
          it.key === itemKey ? { ...it, status, updatedAt: now(), updatedById: user } : it,
        );
        // auto-advance stage on key milestones
        let stage = p.stage;
        if (itemKey === 'om' && status === 'done') stage = maxStage(stage, 'om_sent');
        if (itemKey === 'ela' && status === 'done') stage = maxStage(stage, 'ela_signed');
        if (itemKey === 'listing' && status === 'done') stage = maxStage(stage, 'listed');
        return { ...p, checklist, stage };
      }),
      activity: [
        {
          id: uid(),
          proposalId,
          itemKey,
          authorId: user,
          text: `set “${labelFor(itemKey)}” to ${statusLabel(status)}`,
          createdAt: now(),
        },
        ...s.activity,
      ],
      notifications: [
        {
          id: uid(),
          text: `${get().personById(user)?.name} updated “${labelFor(itemKey)}” → ${statusLabel(status)}`,
          createdAt: now(),
          read: false,
          proposalId,
        },
        ...s.notifications,
      ],
    }));
  },

  addItemLink: (proposalId, itemKey, link) => {
    const user = get().currentUserId;
    const newLink: LinkRef = { ...link, id: uid(), addedAt: now(), addedById: user };
    set((s) => ({
      proposals: s.proposals.map((p) =>
        p.id !== proposalId
          ? p
          : {
              ...p,
              checklist: p.checklist.map((it) =>
                it.key === itemKey ? { ...it, links: [...it.links, newLink], updatedAt: now(), updatedById: user } : it,
              ),
            },
      ),
      activity: [
        {
          id: uid(),
          proposalId,
          itemKey,
          authorId: user,
          text: `attached “${link.label}” to ${labelFor(itemKey)}`,
          createdAt: now(),
        },
        ...s.activity,
      ],
    }));
  },

  addItemNote: (proposalId, itemKey, text) => {
    const user = get().currentUserId;
    const note: NoteEntry = { id: uid(), text, authorId: user, createdAt: now() };
    set((s) => ({
      proposals: s.proposals.map((p) =>
        p.id !== proposalId
          ? p
          : {
              ...p,
              checklist: p.checklist.map((it) =>
                it.key === itemKey ? { ...it, notes: [...it.notes, note], updatedAt: now(), updatedById: user } : it,
              ),
            },
      ),
      activity: [
        { id: uid(), proposalId, itemKey, authorId: user, text: `noted on ${labelFor(itemKey)}: “${text}”`, createdAt: now() },
        ...s.activity,
      ],
    }));
  },

  setProposalStage: (proposalId, stage) =>
    set((s) => ({ proposals: s.proposals.map((p) => (p.id === proposalId ? { ...p, stage } : p)) })),

  togglePropertyPlatform: (propertyId, platform) =>
    set((s) => ({
      properties: s.properties.map((p) => {
        if (p.id !== propertyId) return p;
        const has = p.platforms.includes(platform);
        return { ...p, platforms: has ? p.platforms.filter((x) => x !== platform) : [...p.platforms, platform] };
      }),
    })),

  setPropertyStatus: (propertyId, status) =>
    set((s) => ({ properties: s.properties.map((p) => (p.id === propertyId ? { ...p, status } : p)) })),

  setLeadStatus: (leadId, status) =>
    set((s) => ({ leads: s.leads.map((l) => (l.id === leadId ? { ...l, status } : l)) })),

  addSocialPost: (input) => set((s) => ({ social: [...s.social, { ...input, id: uid() }] })),

  updateSocialStatus: (postId, status) =>
    set((s) => ({ social: s.social.map((p) => (p.id === postId ? { ...p, status } : p)) })),

  moveSocialPost: (postId, date) =>
    set((s) => ({ social: s.social.map((p) => (p.id === postId ? { ...p, date } : p)) })),

  markAllNotificationsRead: () =>
    set((s) => ({ notifications: s.notifications.map((n) => ({ ...n, read: true })) })),
}));

// ---- small helpers ----
const STAGE_ORDER: ProposalStage[] = ['requested', 'in_progress', 'om_sent', 'ela_signed', 'listed', 'live'];
function maxStage(a: ProposalStage, b: ProposalStage): ProposalStage {
  return STAGE_ORDER.indexOf(a) >= STAGE_ORDER.indexOf(b) ? a : b;
}
function labelFor(key: string) {
  return CHECKLIST_TEMPLATE.find((t) => t.key === key)?.label ?? key;
}
function statusLabel(s: StatusKey) {
  return { not_started: 'Not started', working: 'Working on it', stuck: 'Stuck', done: 'Done', na: 'N/A' }[s];
}

export type { Notification };
